import { useTranslation } from 'react-i18next';
import { useEffect, useState, Suspense, lazy, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, BrowserRouter, useNavigate, useLocation } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import { Toaster } from "./components/ui/toaster";
import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css';
import { LoadScript } from '@react-google-maps/api';
import { google_api_keys } from './utils/constants';
import { database, generateTokenByRequestPermission, onValue, ref, onMessageListener } from './config/firebase-config';
import SocketService from './utils/services/socket-service';
import * as UserActions from './redux/actions/userAction';
import * as ChatActions from './redux/actions/chatAction';
import * as CommonActions from './redux/actions/commonAction';
import LifeJourneyReport from "./pages/life-journey-report";
import LifeJourneyReportForm from "./pages/life-journey-report-form";
import OrderConfirmed from "./pages/order-confirmed";
import Reports from "./pages/reports";
import LifeChangingReport from "./pages/life-changing-report";
import LifeChangingReportForm from "./pages/life-changing-report-form";
import KundaliMatchingReport from "./pages/kundali-matching-report";
import KundaliMatchingReportForm from "./pages/kundali-matching-report-form";
import LoveReport from "./pages/love-report";
import LoveReportForm from "./pages/love-report-form";
import NavratriReport from "./pages/navratri-report";

// TODO : Components
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import Loading from './components/features/Loading';
import NotFound from './components/features/NotFound';
import CallInvoiceModal from './components/modal/CallInvoiceModal';
import PageBlock from './components/features/PageBlock';
import ScrollToTop from './components/features/ScrollToTop';
import ProtectedRouteCustomer from './utils/protected-routes/customer';
import ProtectedRouteAstrologer from './utils/protected-routes/astrologer';
import DownloadApp from './components/features/DownloadApp';
import CallRating from './components/features/CallRating';
import Horoscope from './pages/horoscope';
import HoroscopeDetails from './pages/horoscope/HoroscopeDetails';
import AstrologerSignup from './pages/astrologer-dashboard/astrologer-signup';
import AstrologerLoginModal from './components/modal/AstrologerLoginModal';
import CustomerLoginModal from './components/modal/CustomerLoginModal';
import { postAPI } from './utils/api-function';


//import LandingPage from "./pages/landing-page";


//! Lazy Load Pages
const VideoCall = lazy(() => import('./pages/video-call'));
const LandingPage = lazy(() => import('./pages/landing-page'));


const LiveStreaming = lazy(() => import('./pages/live-streaming'));

//! Customer
const CustomerMyAccount = lazy(() => import('./pages/my-account'));
const CustomerMyMessage = lazy(() => import('./pages/my-message'));
const CustomerChatMessage = lazy(() => import('./pages/my-message/chat-message'));
const CustomerTransactionHistory = lazy(() => import('./pages/transaction-history'));
const CustomerChatSummary = lazy(() => import('./pages/transaction-history/chat-summary'));
const WhatsappChatSummary = lazy(() => import('./pages/transaction-history/whatsapp-chat-summary'));
const CustomerWalletHistory = lazy(() => import('./pages/wallet-history'));
const CustomerMyOrder = lazy(() => import('./pages/my-order'));
const MyBooking = lazy(() => import('./pages/my-booking'));

//! Recharge
const Recharge = lazy(() => import('./pages/recharge'));
const PaymentDetail = lazy(() => import('./pages/recharge/payment-detail'));

//! Astrologer Dashboard
const AstrologerMyAccount = lazy(() => import('./pages/astrologer-dashboard/my-account'));
const AstrologerQueuelist = lazy(() => import('./pages/astrologer-dashboard/queue-list'));
const AstrologerMyMessage = lazy(() => import('./pages/astrologer-dashboard/my-message'));
const AstrologerQueueChatMessage = lazy(() => import('./pages/astrologer-dashboard/my-message/queue-chat-message'));
const AstrologerTransactionHistory = lazy(() => import('./pages/astrologer-dashboard/transaction-history'));
const AstrologerWalletHistory = lazy(() => import('./pages/astrologer-dashboard/wallet-history'));
const AstrologerRegisterPujaHistory = lazy(() => import('./pages/astrologer-dashboard/register-puja-history'));
const AstrologerAssignedPujaHistory = lazy(() => import('./pages/astrologer-dashboard/assigned-puja-history'));
const AstrologerBookPujaHistory = lazy(() => import('./pages/astrologer-dashboard/book-puja-history'));
const AstrologerSlotManagement = lazy(() => import('./pages/astrologer-dashboard/consultation/slot-management'));
const AstrologerBookingManagement = lazy(() => import('./pages/astrologer-dashboard/consultation/booking-management'));
// const AstrologerLeaveManagement = lazy(() => import('./pages/astrologer-dashboard/leave-management'));
const AstrologerLiveStreaming = lazy(() => import('./pages/astrologer-dashboard/live-streaming'));

//! Astrologer
const Astrologer = lazy(() => import('./pages/astrologer'));
const AstrologerDetails = lazy(() => import('./pages/astrologer/name'));
const ConsultationCheckout = lazy(() => import('./pages/astrologer/checkout'));

const ChatConsultation = lazy(() => import('./pages/consultation/chat-consultation'));

//! Free Kundli 
const Kundli = lazy(() => import('./pages/kundli'));
const KundliDetails = lazy(() => import('./pages/kundli/kundli-details'));

//! Kundli Matching 
const KundliMatching = lazy(() => import('./pages/kundli-matching'));
const KundliMatchingReports = lazy(() => import('./pages/kundli-matching/reports'));

//! Book Puja
const SuggestedPuja = lazy(() => import('./pages/suggested-puja'));
const BookPuja = lazy(() => import('./pages/book-puja'));
const BookPujaDetails = lazy(() => import('./pages/book-puja/puja-details'));
const RegisterPuja = lazy(() => import('./pages/register-puja'));
const RegisterPujaDetails = lazy(() => import('./pages/register-puja/puja-details'));

const Cart = lazy(() => import('./pages/book-puja/cart'));
const Address = lazy(() => import('./pages/book-puja/address'));

//! Blog 
const Blog = lazy(() => import('./pages/blog'));
const BlogDetails = lazy(() => import('./pages/blog/blog-details'));

//! Important Links
const ImportantLinks = lazy(() => import('./pages/important-links'));
const PlanetaryTransit = lazy(() => import('./pages/astrology-insights/important-links/planetary-transit'));
const FestivalCalendar = lazy(() => import('./pages/astrology-insights/important-links/festival-calendar'));
const VratCalendar = lazy(() => import('./pages/astrology-insights/important-links/vrat-calendar'));
const KaalsarpDoshas = lazy(() => import('./pages/astrology-insights/important-links/kaalsarp-doshas'));
const AstrologyYoga = lazy(() => import('./pages/astrology-insights/important-links/astrology-yoga'));
const LunarEclipse = lazy(() => import('./pages/astrology-insights/important-links/lunar-eclipse'));
const SolarEclipse = lazy(() => import('./pages/astrology-insights/important-links/solar-eclipse'));
const Mantra = lazy(() => import('./pages/astrology-insights/important-links/mantra'));

//! Muhurat
const AnnanprashanMuhurat = lazy(() => import('./pages/astrology-insights/muhurat/annanprashan-muhurat'));
const NaamkaranMuhurat = lazy(() => import('./pages/astrology-insights/muhurat/naamkaran-muhurat'));
const VehicleMuhurat = lazy(() => import('./pages/astrology-insights/muhurat/vehicle-muhurat'));
const BhoomiPujanMuhurat = lazy(() => import('./pages/astrology-insights/muhurat/bhoomi-pujan-muhurat'));
const GrihaPraveshMuhurat = lazy(() => import('./pages/astrology-insights/muhurat/griha-pravesh-muhurat'));
const MundanMuhurat = lazy(() => import('./pages/astrology-insights/muhurat/mundan-muhurat'));
const MarriageMuhurat = lazy(() => import('./pages/astrology-insights/muhurat/marriage-muhurat'));

//! Static Page 
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy'));
const TermsOfUse = lazy(() => import('./pages/terms-of-use'));
const AboutUs = lazy(() => import('./pages/about-us'));

//! Consultation Page
const ConsultationPage = lazy(() => import('./pages/constitution-page'));

const App = () => {
  const location = useLocation()
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const { socketConnectionStatus } = useSelector(state => state?.commonReducer);
  const { userCustomerDataById, userAstrologerDataById } = useSelector(state => state?.userReducer);
  const { webLanguageData } = useSelector(state => state?.commonReducer);
  const { requestInitiatedByCustomer, astrologerRatingVisibility, rejectChatByAstrologer } = useSelector(state => state?.chatReducer);

  const [showHeaderFooter, setShowHeaderFooter] = useState(true);

  useEffect(() => {

    // if (location.pathname == "/life-journey-report" || 
    //   location.pathname == "/life-journey-report/journey-report" || 
    //   location.pathname == "/life-journey-report/journey-report-consultation" || 
    //   location.pathname == "/life-journey-report/journey-report-ask-astrologer" || 
    //   location.pathname == "/order-confirmed" || 
    //   location.pathname == "/life-changing-report" ||
    //   location.pathname == "/life-changing-report/life-changing-report" ||
    //   location.pathname == "/life-changing-report/life-changing-report-consultation" ||
    //   location.pathname == "/life-changing-report/life-changing-report-ask-astrologer" 
    //   ) {
    //   setShowHeaderFooter(false);
    // } else {
    //   setShowHeaderFooter(true);
    // }
    setShowHeaderFooter(true);
  }, [location]);

  useEffect(() => {
    generateTokenByRequestPermission();
    onMessageListener(navigate, dispatch); //! Listen for foreground messages

    // SocketService.initializeSocket(dispatch, navigate);

    if (userCustomerDataById?._id) {
      //! Initialize Socket for customer
      console.log(`Initialize Socket for customer`);
      SocketService.initializeSocket(dispatch, navigate, userCustomerDataById._id, 'Customer');
    } else if (userAstrologerDataById?._id) {
      //! Initialize Socket for astrologer
      console.log(`Initialize Socket for astrologer`);
      SocketService.initializeSocket(dispatch, navigate, userAstrologerDataById?._id, 'Astrologer');
    } else {
      SocketService.initializeSocket(dispatch, navigate, `guest_${Date.now()}`, 'Guest');
    }

  }, [dispatch, navigate, userCustomerDataById, userAstrologerDataById]);

  // useEffect(() => {
  //   userCustomerDataById && dispatch(UserActions?.getUserCustomerCompletedQueueList());
  // }, [userCustomerDataById]);

  // useEffect(() => {
  //   userAstrologerDataById && dispatch(UserActions?.getUserAstrologerPendingQueueList());
  // }, [userAstrologerDataById]);


  const storeCustomerToken = async () => {
    try {
      const { data } = await postAPI('api/customers/update_fcm_token', { customerId: localStorage.getItem('current_user_id'), webFcmToken: localStorage.getItem('fcm_token') })
      console.log('Token Store :::', data);
    } catch (error) {

    }
  }

  const storeAstrologerToken = async () => {
    try {
      const { data } = await postAPI('api/astrologer/update_fcm_token', { astrologerId: localStorage.getItem('current_user_id'), webFcmToken: localStorage.getItem('fcm_token') })
      console.log('Token Store :::', data);
    } catch (error) {

    }
  }

  useEffect(() => {
    const user_type = localStorage.getItem('user_type');
    const current_user_id = localStorage.getItem('current_user_id');


    if (user_type == 'customer') {
      dispatch(UserActions.getUserCustomerById({ customerId: current_user_id }));
      storeCustomerToken();
      const messagesRef = ref(database, `CurrentCall/${current_user_id}`);
      onValue(messagesRef, (snapshot) => {
        const data = snapshot.val();

        if (data) {
          dispatch(ChatActions.callIntakeDetailData({ visible: true, profileId: data?.formId }))
        } else {
          dispatch(ChatActions.callIntakeDetailData({ visible: false, profileId: null }))
        }
      });
    }

    if (user_type == 'astrologer') {
      dispatch(UserActions.getUserAstrologerById({ astrologerId: current_user_id }));
      storeAstrologerToken();
      try {
      } catch (error) {

      }

      const messagesRef = ref(database, `CurrentCall/${current_user_id}`);
      onValue(messagesRef, (snapshot) => {
        const data = snapshot.val();

        if (data) {
          dispatch(ChatActions.callIntakeDetailData({ visible: true, profileId: data?.formId }))
        } else {
          dispatch(ChatActions.callIntakeDetailData({ visible: false, profileId: null }))
        }
      });
    }
  }, []);

  useEffect(() => {
    // console.log("Web Language ::: ", webLanguageData);
    dispatch(CommonActions?.webLanguage(localStorage?.getItem('web_language') || webLanguageData));
    i18n?.changeLanguage(localStorage?.getItem('web_language') || webLanguageData);
  }, [webLanguageData]);

  //! Scrolling 
  const scrollToSection = (sectionId) => {
    console.log("Section ID ::: ", sectionId);
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // If the section is not found, navigate to the home page
      navigate('/');
      setTimeout(() => {
        const homeSection = document.getElementById(sectionId);
        if (homeSection) {
          homeSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const libraries = useMemo(() => ['places', 'geometry'], []);

  return (
    <>
      <LoadScript googleMapsApiKey={google_api_keys} libraries={libraries} loadingElement={<Loading />}>
        <Suspense fallback={<Loading />}>
          {showHeaderFooter && <Header />} {/* Conditionally render the Header */}
          <ScrollToTop>
            <Routes>
              <Route path='*' element={<NotFound />} />
              <Route path='/video-call' element={<VideoCall />} />
              <Route path='/' element={<LandingPage />} />
              <Route path='/live-streaming' element={<LiveStreaming />} />
              <Route path='/astrologer-signup' element={<AstrologerSignup />} />

              {/* Customer */}
              <Route path='/my-account' element={<ProtectedRouteCustomer><CustomerMyAccount /></ProtectedRouteCustomer>} />
              <Route path='/my-message' element={<ProtectedRouteCustomer><CustomerMyMessage /></ProtectedRouteCustomer>} />
              <Route path='/my-message/chat-message' element={<ProtectedRouteCustomer><CustomerChatMessage /></ProtectedRouteCustomer>} />
              <Route path='/transaction-history' element={<ProtectedRouteCustomer><CustomerTransactionHistory /></ProtectedRouteCustomer>} />
              <Route path='/transaction-history/chat-summary' element={<ProtectedRouteCustomer><CustomerChatSummary /></ProtectedRouteCustomer>} />
              <Route path='/transaction-history/whatsapp-chat-summary' element={<WhatsappChatSummary />} />
              <Route path='/wallet-history' element={<ProtectedRouteCustomer><CustomerWalletHistory /></ProtectedRouteCustomer>} />
              <Route path='/my-order' element={<ProtectedRouteCustomer><CustomerMyOrder /></ProtectedRouteCustomer>} />
              <Route path='/my-booking' element={<ProtectedRouteCustomer><MyBooking /></ProtectedRouteCustomer>} />

              {/* Recharge */}
              <Route path='/recharge' element={<ProtectedRouteCustomer><Recharge /></ProtectedRouteCustomer>} />
              <Route path='/recharge/payment-details' element={<ProtectedRouteCustomer><PaymentDetail /></ProtectedRouteCustomer>} />

              {/* Astrologer Dashboard */}
              <Route path='/astrologer-dashboard/my-account' element={<ProtectedRouteAstrologer><AstrologerMyAccount /></ProtectedRouteAstrologer>} />
              <Route path='/astrologer-dashboard/queue-list' element={<ProtectedRouteAstrologer><AstrologerQueuelist /></ProtectedRouteAstrologer>} />
              <Route path='/astrologer-dashboard/my-message' element={<ProtectedRouteAstrologer><AstrologerMyMessage /></ProtectedRouteAstrologer>} />
              <Route path='/astrologer-dashboard/my-message/queue-chat-message' element={<ProtectedRouteAstrologer><AstrologerQueueChatMessage /></ProtectedRouteAstrologer>} />
              <Route path='/astrologer-dashboard/transaction-history' element={<ProtectedRouteAstrologer><AstrologerTransactionHistory /></ProtectedRouteAstrologer>} />
              <Route path='/astrologer-dashboard/wallet-history' element={<ProtectedRouteAstrologer><AstrologerWalletHistory /></ProtectedRouteAstrologer>} />
              <Route path='/astrologer-dashboard/register-puja-history' element={<ProtectedRouteAstrologer><AstrologerRegisterPujaHistory /></ProtectedRouteAstrologer>} />
              <Route path='/astrologer-dashboard/assigned-puja-history' element={<ProtectedRouteAstrologer><AstrologerAssignedPujaHistory /></ProtectedRouteAstrologer>} />
              <Route path='/astrologer-dashboard/book-puja-history' element={<ProtectedRouteAstrologer><AstrologerBookPujaHistory /></ProtectedRouteAstrologer>} />
              <Route path='/astrologer-dashboard/slot-management' element={<ProtectedRouteAstrologer><AstrologerSlotManagement /></ProtectedRouteAstrologer>} />
              <Route path='/astrologer-dashboard/booking-management' element={<ProtectedRouteAstrologer><AstrologerBookingManagement /></ProtectedRouteAstrologer>} />
              <Route path='/astrologer-dashboard/live-streaming' element={<ProtectedRouteAstrologer><AstrologerLiveStreaming /></ProtectedRouteAstrologer>} />

              {/* Astrologer */}
              <Route path='/astrologer' element={<Astrologer />} />
              <Route path='/astrologer/details' element={<AstrologerDetails key={location.search} />} />
              <Route path='/consultation/checkout' element={<ConsultationCheckout />} />
              <Route path='/consultation/chat-consultation' element={<ChatConsultation />} />


              {/* Free Kundli */}
              <Route path='/kundli' element={<Kundli />} />
              <Route path='/kundli/kundli-details' element={<KundliDetails />} />

              {/* Kundli Matching */}
              <Route path='/kundli-matching' element={<KundliMatching />} />
              <Route path='/kundli-matching/reports/:profileId' element={<KundliMatchingReports />} />

              {/* Horoscope */}
              <Route path='/horoscope' element={<Horoscope />} />
              <Route path='/horoscope/:zodiacSign' element={<HoroscopeDetails />} />

              {/* Book Puja */}
              <Route path='/suggested-puja' element={<ProtectedRouteCustomer><SuggestedPuja /></ProtectedRouteCustomer>} />
              <Route path='/book-puja' element={<ProtectedRouteCustomer><BookPuja /></ProtectedRouteCustomer>} />
              <Route path='/book-puja/:name' element={<ProtectedRouteCustomer><BookPujaDetails /></ProtectedRouteCustomer>} />

              {/* Register Puja */}
              <Route path='/register-puja' element={<ProtectedRouteAstrologer><RegisterPuja /></ProtectedRouteAstrologer>} />
              <Route path='/register-puja/:name' element={<ProtectedRouteAstrologer><RegisterPujaDetails /></ProtectedRouteAstrologer>} />
              <Route path='/cart' element={<Cart />} />
              <Route path='/cart/address' element={<Address />} />
              <Route path='/astro-book-store' element={<div className='min-h-40 text-black text-center pt-40 '>Coming Soon....</div>} />

              {/* Important Links */}
              <Route path='/important-links' element={<ImportantLinks />} />
              <Route path='/astrology-insights/planetary-transit' element={<PlanetaryTransit />} />
              <Route path='/astrology-insights/festival-calendar' element={<FestivalCalendar />} />
              <Route path='/astrology-insights/vrat-calendar' element={<VratCalendar />} />
              <Route path='/astrology-insights/kaalsarp-doshas' element={<KaalsarpDoshas />} />
              <Route path='/astrology-insights/astrology-yoga' element={<AstrologyYoga />} />
              <Route path='/astrology-insights/lunar-eclipse' element={<LunarEclipse />} />
              <Route path='/astrology-insights/solar-eclipse' element={<SolarEclipse />} />
              <Route path='/astrology-insights/mantra' element={<Mantra />} />

              {/* Muhurat */}
              <Route path='/muhurat/annanprashan-muhurat' element={<AnnanprashanMuhurat />} />
              <Route path='/muhurat/naamkaran-muhurat' element={<NaamkaranMuhurat />} />
              <Route path='/muhurat/vehicle-muhurat' element={<VehicleMuhurat />} />
              <Route path='/muhurat/bhoomi-pujan-muhurat' element={<BhoomiPujanMuhurat />} />
              <Route path='/muhurat/griha-pravesh-muhurat' element={<GrihaPraveshMuhurat />} />
              <Route path='/muhurat/mundan-muhurat' element={<MundanMuhurat />} />
              <Route path='/muhurat/marriage-muhurat' element={<MarriageMuhurat />} />

              {/* Blog */}
              <Route path='/blog' element={<Blog />} />
              <Route path='/blog/blog-details' element={<BlogDetails />} />

              {/* Pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/consultation-page" element={<ConsultationPage />} />

              {/* Life Journey Report */}
              <Route path="/life-journey-report" element={<LifeJourneyReport />} />
              <Route path="/life-journey-report/:planId" element={<LifeJourneyReportForm />} />
              <Route path="/order-confirmed" element={<OrderConfirmed />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/life-changing-report" element={<LifeChangingReport />} />
              <Route path="/life-changing-report/:planId" element={<LifeChangingReportForm />} />
              <Route path="/kundali-matching-report" element={<KundaliMatchingReport />} />
              <Route path="/kundali-matching-report/:planId" element={<KundaliMatchingReportForm />} />
              <Route path="/love-report" element={<LoveReport />} />
              <Route path="/love-report/:planId" element={<LoveReportForm />} />
              <Route path="/navratri-report" element={<NavratriReport />} />
              
              
            </Routes>
          </ScrollToTop>
        </Suspense>
        {(
          location?.pathname !== '/chat' && 
          location?.pathname !== '/astrologer-dashboard/live-streaming' && 
          location?.pathname !== '/consultation/chat-consultation' && 
          location?.pathname !== '/transaction-history/chat-summary' && 
          location?.pathname !== '/transaction-history/whatsapp-chat-summary' && 
          location?.pathname !== '/life-journey-report' && 
          location.pathname !== "/life-journey-report/journey-report" && 
          location.pathname !== "/life-journey-report/journey-report-consultation" && 
          location.pathname !== "/life-journey-report/journey-report-ask-astrologer" && 
          location.pathname !== "/order-confirmed" && 
          location.pathname !== "/life-changing-report" && 
          location.pathname !== "/life-changing-report/life-changing-report" && 
          location.pathname !== "/life-changing-report/life-changing-report-consultation" && 
          location.pathname !== "/life-changing-report/life-changing-report-ask-astrologer" &&
          location.pathname !== "/kundali-matching-report" && 
          location.pathname !== "/kundali-matching-report/kundali-matching-report" && 
          location.pathname !== "/kundali-matching-report/kundali-matching-report-consultation" && 
          location.pathname !== "/kundali-matching-report/kundali-matching-report-ask-astrologer" && 
          location.pathname !== "/love-report" && 
          location.pathname !== "/love-report/love-report-consultation" && 
          location.pathname !== "/love-report/love-report-ask-astrologer" &&
          location.pathname !== "/navratri-report"
          ) && <Footer scrollToSection={scrollToSection} />}

        <AstrologerLoginModal />
        <CustomerLoginModal />
        <DownloadApp />
        <CallInvoiceModal />
        {requestInitiatedByCustomer?.initiated && <PageBlock />}
        <ToastContainer />
        <Toaster />
        {astrologerRatingVisibility?.type == 'CALL' && <CallRating />}
      </LoadScript>
    </>
  )
}

export default App;